import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { AlertMessage } from '@eposnow/ui-core';
import validationSchema from '../helpers/validationSchema';
import ProductForm from './ProductForm';
import { Product, ProductCreateEdit, ProductErrors } from '../../../../types';
import { UserContext } from '../../../../context/UserContext';
import { PRODUCTS_API_CACHE_KEY } from '../../../../constants';
import useCreateUpdate from '../../../../helperHooks/useCreateUpdate';
import { convertObjectToStringValues } from '../../../../helpers/helpers';
import CreateEditCard from '../../../../components/createEditComponents/CreateEditCard';
import { API_V1_HOSTNAME_KEY } from '../../../../localStorageKeys';

export const emptyObject: ProductCreateEdit = {
  id: null,
  name: '',
  costPrice: null,
  salePrice: null,
  categoryId: null,
  buttonColourId: null,
};

export type CreateEditProductCardProps = {
  currentObject: Product;
  resetObject: () => void;
  setSuccessMessage: (message: string) => void;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  errors: ProductErrors;
  setErrors: (obj: any) => void;
  mode: string;
};

const CreateEditProductCard = ({
  currentObject,
  resetObject,
  setSuccessMessage,
  errorMessage,
  setErrorMessage,
  errors,
  setErrors,
  mode,
}: CreateEditProductCardProps) => {
  const [initialObject, setInitialObject] = useState<ProductCreateEdit>(emptyObject);
  const [objectId, setObjectId] = useState<number>(0);
  const { t } = useTranslation();
  const theme = useTheme();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const { locale } = useContext(UserContext);

  const removeSavedObject = () => {
    const savedObjects = JSON.parse(sessionStorage.getItem(PRODUCTS_API_CACHE_KEY) || '{}');
    delete savedObjects[`${objectId}`];
    sessionStorage.setItem(PRODUCTS_API_CACHE_KEY, JSON.stringify(savedObjects));
  };

  const { isLoading, apiCall } = useCreateUpdate(
    mode,
    'products.products',
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/products`,
    'products',
    PRODUCTS_API_CACHE_KEY,
    resetObject,
    removeSavedObject,
    setSuccessMessage,
    setErrorMessage,
    setErrors,
    mode === 'create' ? '0' : currentObject.id.toString()
  );

  useEffect(() => {
    const savedObjects = JSON.parse(sessionStorage.getItem(PRODUCTS_API_CACHE_KEY) || '{}');
    const newObjectId = mode === 'create' ? 0 : currentObject.id;
    setObjectId(newObjectId);
    const savedObject = savedObjects[`${newObjectId}`];

    let newInitialObject: ProductCreateEdit = { ...emptyObject, id: 0, ...savedObject };
    if (mode === 'create') {
      sessionStorage.setItem(
        PRODUCTS_API_CACHE_KEY,
        JSON.stringify({
          ...savedObjects,
          [`${newObjectId}`]: newInitialObject,
        })
      );
    } else {
      const parsedCurrentObject = { ...currentObject };
      newInitialObject = {
        ...emptyObject,
        id: parsedCurrentObject?.id,
        name: parsedCurrentObject?.Name,
        salePrice: parsedCurrentObject?.SalePriceIncTax,
        costPrice: parsedCurrentObject?.CostPriceIncTax,
        categoryId: parsedCurrentObject?.CategoryId,
        buttonColourId: parsedCurrentObject?.ButtonColourId,
      };
    }

    setInitialObject(newInitialObject);
  }, [currentObject]);

  const formik = useFormik({
    initialValues: {
      ...initialObject,
      salePrice:
        (initialObject.salePrice || initialObject.salePrice === 0) &&
        !Number.isNaN(initialObject.salePrice)
          ? parseFloat(initialObject.salePrice?.toFixed(2))
          : null,
      costPrice:
        (initialObject.costPrice || initialObject.costPrice === 0) &&
        !Number.isNaN(initialObject.costPrice)
          ? parseFloat(initialObject.costPrice?.toFixed(2))
          : null,
      categoryId: initialObject?.categoryId?.toString() || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (isLoading) return;

      const requestValues: ProductCreateEdit = {
        ...values,
        id: initialObject?.id,
        categoryId: parseInt(values?.categoryId, 10) || null,
      };

      actions.setSubmitting(false);

      if (mode === 'create') {
        apiCall([requestValues]);
      } else if (
        JSON.stringify(convertObjectToStringValues(requestValues)) !==
        JSON.stringify(convertObjectToStringValues(initialObject))
      ) {
        apiCall({ oldObject: initialObject, newObject: requestValues });
      }
    },
  });

  const clearError = (errorKey: string) => {
    setErrors(({ [errorKey as keyof ProductErrors]: _value, ...remaining }) => remaining);
  };

  useEffect(() => {
    if (formik.values?.id == null) return;
    const savedObject = mode === 'create' ? { ...emptyObject, id: 0 } : currentObject;
    setHasUnsavedChanges(
      JSON.stringify(convertObjectToStringValues(formik.values)) !==
        JSON.stringify(convertObjectToStringValues(savedObject))
    );
  }, [JSON.stringify(formik.values)]);

  return (
    <CreateEditCard
      resetObject={resetObject}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      mode={mode}
      translationKey="products.products"
    >
      <Box padding={2}>
        <ProductForm
          formik={formik}
          cancelAction={() => {
            resetObject();
            removeSavedObject();
          }}
          objectId={objectId.toString()}
          isSubmitting={isLoading}
          errors={errors}
          clearError={clearError}
        />
        {hasUnsavedChanges && (
          <AlertMessage type="warning" locale={locale} theme={theme}>
            {t('errors.unsavedChanges')}
          </AlertMessage>
        )}
      </Box>
    </CreateEditCard>
  );
};

export default CreateEditProductCard;
